import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';

import MenuItem from 'material-ui/MenuItem';
import AutoComplete from 'material-ui/AutoComplete';
import { RadioButton } from 'material-ui/RadioButton';

import { getGradeSettingsFromFirstSeniorGrade } from '../../utils/gradeSettings';
import unflatten from '../../utils/unflatten';

import Form from '../../UI/Form';
import FormTextField from '../../UI/FormTextField';
import FormAutoComplete from '../../UI/FormAutoComplete';
import FormSelectField from '../../UI/FormSelectField';
import FormCheckbox from '../../UI/FormCheckbox';
import FormRadioButtonGroup from '../../UI/FormRadioButtonGroup';
import FormRaisedButton from '../../UI/FormRaisedButton';

import styles from './TownForm.css';

const regionSource = ['Москва', 'Россия', 'экс-СССР', 'Сербия', 'Корея', 'Австралия и океания', 'Иран'];

class TownInfo extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        regList: PropTypes.object,
    };

    state = {};

    constructor(props) {
        super(props);

        const { regList = {} } = props;

        let form = {
            registeredPapers: {
                p: Array(14).fill(0),
                m: Array(14).fill(0),
                t: Array(14).fill(0),
                v: Array(14).fill(0),
            },
            gradeChangesAtWinter: false,
            defaultGradeSettings: true,
            progress: {
                fallRegistered: false,
                fallVerified: false,
                fallReportSent: false,
                fallReportVerified: false,
                springRegistered: false,
                springVerified: false,
                springReportSent: false,
                springReportVerified: false,
                dispatched: false,
            },
            'name.local': regList.townName,
            language: 'ru',
            population: regList.population,
            'contacts.email': regList.email,
            'contacts.phone': regList.phone,
            'contacts.person': regList.addressee,
            'contacts.address': regList.address,
            juryPresident: regList.juryHeadName,
            organizingCommitteePresident: regList.orgHeadName,
        };

        this.state = { form, error: '' };
    }

    handleSubmit = form => {
        let { defaultGradeSettings, firstSeniorGrade, ...town } = form;

        this.setState({ saving: true });

        town = unflatten(town);

        if (defaultGradeSettings) {
            town.gradeSettings = [];
        } else {
            town.gradeSettings = getGradeSettingsFromFirstSeniorGrade(firstSeniorGrade);
        }

        this.props.onCreateTown(town);
    };

    handleFormValuesUpdate = form => {
        const { townDataSource } = this.props;

        if (townDataSource && this.state.form['name.ru'] !== form['name.ru']) {
            const town = townDataSource.find(({ text }) => text === form['name.ru']);

            if (town) {
                const data = town.value;

                form = {
                    ...form,
                    'contacts.address': data.contacts.address,
                    'contacts.email': data.contacts.email,
                    'contacts.person': data.contacts.person,
                    'contacts.phone': data.contacts.phone,
                    defaultGradeSettings: !data.gradeSettings.length,
                    firstSeniorGrade: data.gradeSettings.length
                        ? data.gradeSettings.find(({ level }) => level === 'senior').number
                        : 10,
                    gradeChangesAtWinter: data.gradeChangesAtWinter,
                    juryPresident: data.juryPresident,
                    language: data.language,
                    'name.en': data.name.en,
                    'name.genitive': data.name.genitive,
                    'name.local': data.name.local,
                    'name.ru': data.name.ru,
                    organizingCommitteePresident: data.organizingCommitteePresident,
                    population: data.population,
                    region: data.region,
                };
            }
        }

        this.setState({ form });
    };

    render() {
        const {
            intl: { formatMessage },
            regList,
            townDataSource,
        } = this.props;
        const { form, saving } = this.state;

        return (
            <Form
                onFormValuesUpdate={this.handleFormValuesUpdate}
                form={form}
                editable
                labelIdPrefix="RegisterTown"
                onSubmit={this.handleSubmit}
            >
                <h3 className={styles.subheader}>
                    <FormattedMessage id="RegListAdmin.town info" />
                </h3>
                {!townDataSource && <FormTextField name="name.ru" />}
                {townDataSource && <FormAutoComplete name="name.ru" dataSource={townDataSource} />}
                <FormTextField name="name.local" />
                <FormTextField name="name.en" />
                <FormTextField name="comment" multiLine />
                <FormAutoComplete
                    name="region"
                    dataSource={regionSource}
                    filter={AutoComplete.caseInsensitiveFilter}
                    openOnFocus
                />
                <FormSelectField name="language">
                    <MenuItem value="ru" primaryText="русский" />
                    <MenuItem value="en" primaryText="английский" />
                    <MenuItem value="es" primaryText="испанский" />
                    <MenuItem value="zh" primaryText="китайский" />
                    <MenuItem value="ko" primaryText="корейский" />
                    <MenuItem value="de" primaryText="немецкий" />
                    <MenuItem value="pl" primaryText="польский" />
                    <MenuItem value="sr" primaryText="сербский" />
                </FormSelectField>
                <FormTextField name="population" numberOnly />
                <h3 className={styles.subheader}>
                    <FormattedMessage id="RegList.contact info" />
                </h3>
                <FormTextField name="contacts.person" />
                <FormTextField name="contacts.email" />
                <FormTextField name="contacts.phone" />
                <FormTextField name="contacts.address" multiLine />
                <h3 className={styles.subheader}>
                    <FormattedMessage id="RegisterTown.diploma info" />
                </h3>
                <FormTextField name="name.genitive" />
                <FormTextField name="organizingCommitteePresident" />
                <FormTextField name="juryPresident" />
                <h3 className={styles.subheader}>
                    <FormattedMessage id="RegisterTown.grade settings" />
                </h3>
                <FormRadioButtonGroup name="gradeChangesAtWinter">
                    <RadioButton
                        value={false}
                        label={formatMessage({ id: 'RegisterTown.grade changes at summer' })}
                        className={styles.radioButton}
                        checked={!form.gradeChangesAtWinter}
                    />
                    <RadioButton
                        value={true}
                        label={formatMessage({ id: 'RegisterTown.grade changes at winter' })}
                        className={styles.radioButton}
                        checked={form.gradeChangesAtWinter}
                    />
                </FormRadioButtonGroup>
                <br />
                {!!regList.lastGrade && (
                    <Fragment>
                        <FormattedMessage id="RegisterTown.last grade" />: {regList.lastGrade}
                    </Fragment>
                )}
                <FormCheckbox name="defaultGradeSettings" className={styles.checkbox} />
                {!form.defaultGradeSettings && <FormTextField name="firstSeniorGrade" numberOnly />}
                <FormRaisedButton
                    primary
                    action="submit"
                    label={formatMessage({ id: 'RegList.save' })}
                    className={styles.button}
                    disabled={saving}
                />
            </Form>
        );
    }
}

export default injectIntl(TownInfo);



// WEBPACK FOOTER //
// ./src/Components/TownForm/TownForm.js